import { toPng } from 'html-to-image';
import type { ArtifactSpec, Message } from '../../../flora/flora.type';
import { isContentBlockArray } from '../../../flora/components/flora-message/flora-message.helpers';
import type { InlineNode, MarkdownBlock, TableAlignment } from './agent-chat-export.type';

// ─── Inline Markdown Parsing ─────────────────────────────────────────────────

/**
 * Parses a plain text string into an array of inline nodes, detecting
 * bold+italic (***), bold (**), inline code (`), and italic (*) formatting.
 * Returns a single plain node if no formatting is found.
 *
 * @param {string} text - Raw text string potentially containing inline markdown formatting
 * @returns {InlineNode[]} Array of typed inline nodes for rendering
 */
function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  const pattern = /(\*\*\*([^*\n]+?)\*\*\*|\*\*([^*\n]+?)\*\*|`([^`\n]+?)`|\*([^*\n]+?)\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'plain', text: text.slice(lastIndex, match.index) });
    }
    if (match[2] !== undefined) {
      nodes.push({ type: 'bold_italic', text: match[2] });
    } else if (match[3] !== undefined) {
      nodes.push({ type: 'bold', text: match[3] });
    } else if (match[4] !== undefined) {
      nodes.push({ type: 'code', text: match[4] });
    } else if (match[5] !== undefined) {
      nodes.push({ type: 'italic', text: match[5] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push({ type: 'plain', text: text.slice(lastIndex) });
  }

  return nodes.length === 0 ? [{ type: 'plain', text }] : nodes;
}

/**
 * Splits a pipe-delimited table row into cells and parses each cell's
 * inline formatting. Strips leading/trailing pipes before splitting.
 *
 * @param {string} line - A single markdown table row (e.g. `| Cell 1 | Cell 2 |`)
 * @returns {InlineNode[][]} Array of cells, each containing parsed inline nodes
 */
function parseTableRow(line: string): InlineNode[][] {
  const stripped = line.replace(/^\|/, '').replace(/\|$/, '');
  return stripped.split('|').map((cell) => parseInline(cell.trim()));
}

/**
 * Parses a markdown table separator line (e.g. `|:---|---:|:---:|`)
 * to determine column alignments. Detects left, right, and center
 * alignment based on colon placement.
 *
 * @param {string} separatorLine - The separator row between table header and body
 * @returns {TableAlignment[]} Array of alignment values per column
 */
function parseAlignments(separatorLine: string): TableAlignment[] {
  const stripped = separatorLine.replace(/^\|/, '').replace(/\|$/, '');
  return stripped.split('|').map((cell) => {
    const trimmed = cell.trim();
    const left = trimmed.startsWith(':');
    const right = trimmed.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

// ─── Block-Level Markdown Parsing ────────────────────────────────────────────

/**
 * Parses a markdown string into an array of block-level elements including
 * headings, paragraphs, ordered/unordered list items, fenced code blocks,
 * tables, and horizontal rules. Each block contains parsed inline nodes
 * for rich text rendering in react-pdf.
 *
 * @param {string} markdown - Raw markdown string to parse
 * @returns {MarkdownBlock[]} Array of typed block-level elements
 */
function parseMarkdownBlocks(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const lines = markdown.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // Fenced code block
    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: 'code_block', code: codeLines.join('\n') });
      i++; // skip closing ```
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#+)\s+(.+)$/);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 3) as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, inline: parseInline(headingMatch[2]) });
      i++;
      continue;
    }

    // Unordered list item
    const unorderedMatch = line.match(/^(\s*)[-*+]\s+(.+)$/);
    if (unorderedMatch) {
      const indent = Math.floor(unorderedMatch[1].length / 2);
      blocks.push({ type: 'list_item', ordered: false, indent, inline: parseInline(unorderedMatch[2]) });
      i++;
      continue;
    }

    // Ordered list item
    const orderedMatch = line.match(/^(\s*)\d+[.)]\s+(.+)$/);
    if (orderedMatch) {
      const indent = Math.floor(orderedMatch[1].length / 2);
      blocks.push({ type: 'list_item', ordered: true, indent, inline: parseInline(orderedMatch[2]) });
      i++;
      continue;
    }

    // Table — line contains `|` and next line is a separator row like `|---|---|`
    if (
      line.includes('|') &&
      i + 1 < lines.length &&
      /^\|?[\s:]*-{2,}[\s:]*(\|[\s:]*-{2,}[\s:]*)+\|?$/.test(lines[i + 1])
    ) {
      const headers = parseTableRow(line);
      const alignments = parseAlignments(lines[i + 1]);
      i += 2;
      const rows: InlineNode[][][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(parseTableRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', headers, alignments, rows });
      continue;
    }

    // Horizontal rule — three or more -, *, or _ (but NOT a table separator)
    if (/^(\s*[-*_]\s*){3,}$/.test(line.trim())) {
      blocks.push({ type: 'horizontal_rule' });
      i++;
      continue;
    }

    // Paragraph — collect adjacent non-special lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('```') &&
      !lines[i].match(/^(\s*)[-*+]\s/) &&
      !lines[i].match(/^(\s*)\d+[.)]\s/) &&
      !(
        lines[i].includes('|') &&
        i + 1 < lines.length &&
        /^\|?[\s:]*-{2,}[\s:]*(\|[\s:]*-{2,}[\s:]*)+\|?$/.test(lines[i + 1])
      ) &&
      !/^(\s*[-*_]\s*){3,}$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', inline: parseInline(paraLines.join(' ')) });
    }
  }

  return blocks;
}

// ─── Text Extraction ─────────────────────────────────────────────────────────

/**
 * Extracts the markdown text content from an assistant message.
 * Handles both plain string content and ContentBlock arrays,
 * filtering for text blocks and joining them with double newlines.
 *
 * @param {Message} message - The assistant message to extract text from
 * @returns {string} Markdown text content of the message
 */
function getAnswerMarkdown(message: Message): string {
  if (typeof message.content === 'string') return message.content;
  if (!isContentBlockArray(message.content)) return '';
  return message.content
    .filter((block): block is Extract<typeof block, { type: 'text' }> => block.type === 'text')
    .map((block) => block.text)
    .join('\n\n');
}

/**
 * Extracts the plain text from a user message, stripping any
 * @[mention] syntax used in the chat interface.
 *
 * @param {Message} message - The user message to extract text from
 * @returns {string} Plain text content with mentions removed
 */
function getQuestionText(message: Message): string {
  if (typeof message.content === 'string') {
    return message.content.replace(/@\[[^\]]*\]/g, '').trim();
  }
  return '';
}

// ─── Artifact Capture ────────────────────────────────────────────────────────

/**
 * Converts a base64-encoded data URL to a blob URL.
 * Used to avoid passing data URLs to @react-pdf/renderer's Image component,
 * which internally uses Node's Buffer (unavailable in browsers) to decode them.
 *
 * @param {string} dataUrl - A base64-encoded data URL (e.g. `data:image/png;base64,...`)
 * @returns {string} A blob URL that can be fetched via standard browser APIs
 */
function dataUrlToBlobUrl(dataUrl: string): string {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const bytes = atob(base64);
  const array = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    array[i] = bytes.charCodeAt(i);
  }
  const blob = new Blob([array], { type: mime });
  return URL.createObjectURL(blob);
}

/**
 * Captures an artifact from the live DOM by locating the already-rendered
 * element via its `id="artifact-{id}"` attribute and screenshotting it.
 * This avoids re-rendering complex components (ReactFlow, chart libraries)
 * in a hidden container where they fail to measure dimensions.
 *
 * @param {ArtifactSpec} artifact - The artifact specification to capture
 * @returns {Promise<string | null>} Blob URL of the captured PNG, or null if capture fails
 */
async function captureArtifact(artifact: ArtifactSpec): Promise<string | null> {
  const element = document.getElementById(`artifact-${artifact.id}`);
  if (!element) return null;

  // Track removed elements so we can restore them after capture.
  const removed: { el: Element; parent: Node; nextSibling: Node | null }[] = [];

  try {
    // If the artifact contains a ReactFlow graph, click the built-in fit-view
    // button so the graph is recentered before capture.
    const fitViewBtn = element.querySelector<HTMLButtonElement>('.react-flow__controls-fitview');
    if (fitViewBtn) {
      fitViewBtn.click();
      // Allow the viewport transition to settle.
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    // Strip <animateMotion> elements before capture — ReactFlow animated edge
    // dots cannot render in a static screenshot and cause capture issues.
    // We record each element's position so it can be restored afterward.
    const motionEls = [...element.querySelectorAll('animateMotion')];
    for (const el of motionEls) {
      removed.push({ el, parent: el.parentNode!, nextSibling: el.nextSibling });
      el.remove();
    }

    const dataUrl = await toPng(element, {
      pixelRatio: 1.5,
      backgroundColor: '#ffffff',
    });
    return dataUrlToBlobUrl(dataUrl);
  } catch (err) {
    console.error('[Export] Failed to capture artifact:', artifact.id, err);
    return null;
  } finally {
    // Restore removed elements to the live DOM.
    for (const { el, parent, nextSibling } of removed) {
      parent.insertBefore(el, nextSibling);
    }
  }
}

export { parseMarkdownBlocks, getAnswerMarkdown, getQuestionText, captureArtifact };
